import { DatePipe, DecimalPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { MapsAPILoader } from '@ng-maps/core';
import { TranslateService } from '@ngx-translate/core';
import { v4 as uuidv4 } from 'uuid';

import { MapPoint } from '../../models/map-point.model';

/**
 * Mapa interactivo genérico sobre Google Maps, con clustering. A diferencia de `gmap-view`
 * (acoplado a `Order` y con contenedor fijo), usa un `id` por instancia y redibuja los markers
 * en `ngOnChanges`. El click sobre un marker sólo emite `select`: el detalle lo resuelve el padre.
 */
@Component({
  selector: 'silobag-map',
  templateUrl: './silobag-map.component.html',
  styleUrls: ['./silobag-map.component.scss'],
  providers: [DatePipe, DecimalPipe]
})
export class SilobagMapComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() public points: MapPoint[] = [];
  @Input() public height = '480px';
  /** Centro por defecto cuando no hay puntos con coordenadas válidas (Argentina). */
  @Input() public defaultCenter: { lat: number, lng: number } = { lat: -34.0, lng: -63.0 };
  @Input() public defaultZoom = 4;

  @Output('select') readonly onSelect = new EventEmitter<string>();

  public readonly uuid: string = 'silobag-map-' + uuidv4();

  private map: google.maps.Map;
  private markers: google.maps.marker.AdvancedMarkerElement[] = [];
  private markersById: Map<string, google.maps.marker.AdvancedMarkerElement> = new Map();
  private clusterer: MarkerClusterer | null = null;
  /** Listener one-shot del acercamiento con un único punto; se cancela en cada re-render. */
  private zoomListener: google.maps.MapsEventListener | null = null;
  private ready = false;

  /** Estado del botón de clustering, siempre arranca agrupando (no persiste entre sesiones). */
  private clusteringEnabled = true;
  private clusterToggleButton: HTMLButtonElement | null = null;

  /** Única instancia reusada para la tarjeta de hover: se reposiciona en vez de recrearse. */
  private hoverWindow: google.maps.InfoWindow | null = null;
  /**
   * `mouseleave` puede llegar antes del `mouseenter` del marker vecino cuando el cursor
   * pasa rápido de uno a otro: el cierre se demora un tick para que el siguiente enter lo cancele.
   */
  private hoverCloseTimeout: ReturnType<typeof setTimeout> | null = null;

  private static readonly PIN_COLORS: Record<string, { background: string, border: string, glyphColor: string }> = {
    alert:     { background: '#e6483d', border: '#a6291f', glyphColor: '#ffffff' },
    committed: { background: '#f2a83b', border: '#b2760f', glyphColor: '#ffffff' },
    available: { background: '#3fa876', border: '#256b4a', glyphColor: '#ffffff' },
    muted:     { background: '#9aa1ab', border: '#6b727b', glyphColor: '#ffffff' }
  };

  constructor(
    private mapsAPILoader: MapsAPILoader,
    private zone: NgZone,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe
  ) { }

  ngAfterViewInit(): void {
    this.mapsAPILoader.load().then(() => {
      this.map = new google.maps.Map(document.getElementById(this.uuid), {
        fullscreenControl: true,
        mapTypeControl: true,
        streetViewControl: false,
        clickableIcons: false,
        maxZoom: 17,
        minZoom: 3,
        mapId: this.uuid,
        center: this.defaultCenter,
        zoom: this.defaultZoom,
        mapTypeId: google.maps.MapTypeId.SATELLITE
      });

      // `headerDisabled` saca la "x" de cierre por defecto: la tarjeta se cierra sólo con el mouse.
      this.hoverWindow = new google.maps.InfoWindow({ headerDisabled: true, disableAutoPan: true });

      this.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(this.buildClusterToggleControl());

      this.ready = true;
      this.render();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['points'] && this.ready) {
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.clearMarkers();
  }

  private get validPoints(): MapPoint[] {
    return (this.points || []).filter(p =>
      typeof p.lat === 'number' && typeof p.lng === 'number' &&
      Number.isFinite(p.lat) && Number.isFinite(p.lng) &&
      !(p.lat === 0 && p.lng === 0)
    );
  }

  private render(): void {
    this.clearMarkers();

    const points = this.validPoints;
    if (!points.length) {
      this.map.setCenter(this.defaultCenter);
      this.map.setZoom(this.defaultZoom);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    // Jitter determinístico para no superponer marcadores que comparten coordenada exacta.
    const seen: Record<string, number> = {};

    points.forEach(point => {
      const key = point.lat + ',' + point.lng;
      const offset = seen[key] ?? 0;
      seen[key] = offset + 1;

      const position = {
        lat: point.lat + offset * 0.0002,
        lng: point.lng + offset * 0.0002
      };

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position,
        map: this.map,
        content: this.buildPin(point).element,
        title: point.title
      });

      // Los listeners de Google Maps corren fuera de la zona de Angular: sin `zone.run` la vista no se refresca.
      marker.addListener('click', () => {
        this.zone.run(() => this.onSelect.emit(point.id));
      });

      // `AdvancedMarkerElement` es un elemento del DOM: acepta eventos nativos además de `addListener`.
      if (point.hover) {
        marker.addEventListener('mouseenter', () => this.showHover(point, marker));
        marker.addEventListener('mouseleave', () => this.scheduleHoverClose());
      }

      bounds.extend(position);
      this.markers.push(marker);
      this.markersById.set(point.id, marker);
    });

    // Los `AdvancedMarkerElement` ya se agregaron al mapa individualmente arriba: si el
    // clustering está desactivado, alcanza con no instanciar el clusterer para verlos sueltos.
    if (this.clusteringEnabled) {
      this.clusterer = new MarkerClusterer({ map: this.map, markers: this.markers });
    }

    this.map.setCenter(bounds.getCenter());
    this.map.fitBounds(bounds);
    if (points.length === 1) {
      // Con un único punto, `bounds` tiene área cero: fitBounds no da un zoom útil por sí
      // solo, así que forzamos un acercamiento razonable una vez que el mapa termina de ajustarse.
      this.zoomListener = google.maps.event.addListenerOnce(this.map, 'idle', () => {
        this.zoomListener = null;
        this.map.setZoom(15);
      });
    }
  }

  private clearMarkers(): void {
    this.closeHover();

    // `clearMarkers()` sólo vacía el clusterer: hace falta el `setMap(null)` para
    // desmontarlo del mapa junto con su listener de `idle`.
    this.clusterer?.clearMarkers();
    this.clusterer?.setMap(null);
    this.clusterer = null;

    if (this.zoomListener) {
      google.maps.event.removeListener(this.zoomListener);
      this.zoomListener = null;
    }

    this.markers.forEach(marker => marker.map = null);
    this.markers = [];
    this.markersById.clear();
  }

  /** Botón nativo del mapa (mismo estilo que fullscreen/mapType) para agrupar/desagrupar marcadores. */
  private buildClusterToggleControl(): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'silobag-map-cluster-toggle';

    button.addEventListener('click', () => {
      this.zone.run(() => {
        this.clusteringEnabled = !this.clusteringEnabled;
        this.updateClusterToggleControl();
        this.render();
      });
    });

    this.clusterToggleButton = button;
    this.updateClusterToggleControl();
    return button;
  }

  private updateClusterToggleControl(): void {
    if (!this.clusterToggleButton) return;

    const key = this.clusteringEnabled ? 'SILO_MONITORING.MAP_CLUSTERING_ON' : 'SILO_MONITORING.MAP_CLUSTERING_OFF';
    const label = this.translate.instant(key);

    this.clusterToggleButton.title = label;
    this.clusterToggleButton.setAttribute('aria-label', label);
    this.clusterToggleButton.setAttribute('aria-pressed', String(!this.clusteringEnabled));
    this.clusterToggleButton.classList.toggle('is-active', !this.clusteringEnabled);
    // `scatter_plot` = agrupado (acción disponible: desagrupar), `workspaces` = desagrupado (acción: agrupar).
    this.clusterToggleButton.innerHTML =
      '<i class="material-symbols-rounded">' + (this.clusteringEnabled ? 'scatter_plot' : 'workspaces') + '</i>';
  }

  /** Centra el mapa en el punto indicado, sin emitir `select`. */
  public focusPoint(id: string): void {
    const marker = this.markersById.get(id);
    if (!this.ready || !marker) return;

    this.map.panTo(marker.position as google.maps.LatLngLiteral);
    if ((this.map.getZoom() ?? 0) < 15) this.map.setZoom(15);
  }

  private showHover(point: MapPoint, marker: google.maps.marker.AdvancedMarkerElement): void {
    if (!this.hoverWindow || !point.hover) return;

    if (this.hoverCloseTimeout) {
      clearTimeout(this.hoverCloseTimeout);
      this.hoverCloseTimeout = null;
    }

    this.zone.run(() => {
      this.hoverWindow.setContent(this.buildHoverContent(point));
      this.hoverWindow.open({ map: this.map, anchor: marker });
    });
  }

  /** Da un tick de gracia antes de cerrar, para no parpadear al pasar de un marker a otro. */
  private scheduleHoverClose(): void {
    this.hoverCloseTimeout = setTimeout(() => this.closeHover(), 80);
  }

  private closeHover(): void {
    if (this.hoverCloseTimeout) {
      clearTimeout(this.hoverCloseTimeout);
      this.hoverCloseTimeout = null;
    }
    this.hoverWindow?.close();
  }

  /**
   * Arma el contenido de la tarjeta de hover con el DOM API (no innerHTML): los valores vienen
   * de Siloreal y no deben interpretarse como HTML.
   */
  private buildHoverContent(point: MapPoint): HTMLElement {
    const hover = point.hover;
    const t = (key: string) => this.translate.instant('SILO_MONITORING.MAP_HOVER.' + key);

    const el = (tag: string, className: string, text?: string): HTMLElement => {
      const node = document.createElement(tag);
      node.className = className;
      if (text != null) node.textContent = text;
      return node;
    };

    const card = el('div', 'silobag-hover-card');

    const header = el('div', 'silobag-hover-card__header');
    const icon = el('span', 'silobag-hover-card__icon');
    icon.innerHTML = '<i class="material-symbols-rounded">link</i>';
    const titleBox = el('div', 'silobag-hover-card__title-box');
    titleBox.appendChild(el('div', 'silobag-hover-card__title', point.title));
    titleBox.appendChild(el('div', 'silobag-hover-card__id', t('ID_LABEL') + ' ' + hover.code));
    header.appendChild(icon);
    header.appendChild(titleBox);
    card.appendChild(header);

    const grid = el('div', 'silobag-hover-card__grid');
    const field = (label: string, value: string) => {
      const box = el('div', 'silobag-hover-card__field');
      box.appendChild(el('span', 'silobag-hover-card__label', label));
      box.appendChild(el('span', 'silobag-hover-card__value', value || '—'));
      return box;
    };

    grid.appendChild(field(t('SPECIES'), hover.species));
    grid.appendChild(field(t('PRODUCT'), t('PRODUCT_VALUE')));
    grid.appendChild(field(t('BAGGED'), this.formatHoverDate(hover.baggedDate)));
    grid.appendChild(field(this.translate.instant('SILO_BAGS.LOT_NAME'), hover.lot));
    grid.appendChild(field(t('FIELD_STOCK'), this.formatHoverStock(hover.stockKg)));
    grid.appendChild(field(this.translate.instant('SILO_MONITORING.HARVEST'), hover.harvest));
    card.appendChild(grid);

    return card;
  }

  private formatHoverDate(date: string | null): string {
    if (!date) return '—';
    // Se parsea por partes (no `new Date(string)`) para no correr un día por interpretación UTC.
    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) return '—';

    const lang = this.translate.currentLang;
    const locale = lang && lang !== 'es' ? lang : undefined;
    const format = this.translate.instant('GLOBAL.DATE_FORMAT.MD');
    const formatted = this.datePipe.transform(new Date(year, month - 1, day), format, undefined, locale);
    return formatted ? formatted.toUpperCase() : '—';
  }

  /** Mismo formato que la columna "Peso embolsado" de la tabla: toneladas, 2 decimales, sufijo "tn". */
  private formatHoverStock(stockKg: number | null): string {
    if (stockKg == null || !Number.isFinite(stockKg)) return '—';

    const lang = this.translate.currentLang;
    const locale = lang && lang !== 'es' ? lang : undefined;
    const tons = stockKg / 1000;
    return (this.decimalPipe.transform(tons, '1.0-2', locale) || tons.toString()) + ' tn';
  }

  private buildPin(point: MapPoint): google.maps.marker.PinElement {
    const colors = SilobagMapComponent.PIN_COLORS[point.variant || 'available'];
    return new google.maps.marker.PinElement({
      background: colors.background,
      borderColor: colors.border,
      glyphColor: colors.glyphColor
    });
  }
}
